import {
  createContext,
  useContext,
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useCallback,
} from "react";
import common from "../../helper/common";
import { rebuildAppRouter } from "../../router";
import SignalRService from "../../services/signalR/signalRService";
import { handleClubChange, handlePermissionChange } from "./authHandlers";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [routesReady, setRoutesReady] = useState(false);
  const signalRServiceRef = useRef(null);
  const currentClubIdRef = useRef(null);

  /**
   * Handle club change callback wrapper
   */
  const handleClubChangeCallback = useCallback(
    async (newClubId) => {
      await handleClubChange(
        newClubId,
        user,
        setUser,
        currentClubIdRef,
        setRoutesReady
      );
    },
    [user]
  );

  /**
   * Handle permission change callback wrapper
   */
  const handlePermissionChangeCallback = useCallback(
    async (finalResult, message) => {
      await handlePermissionChange(
        finalResult,
        message,
        user,
        setUser,
        currentClubIdRef,
        setRoutesReady
      );
    },
    [user]
  );

  /**
   * Initialize SignalR connection for real-time notifications
   */
  useEffect(() => {
    if (!user?.id) {
      // Disconnect SignalR if user is logged out
      if (signalRServiceRef.current) {
        signalRServiceRef.current.disconnect();
        signalRServiceRef.current = null;
      }
      return;
    }

    const initializeSignalR = async () => {
      try {
        const signalRService = SignalRService.getInstance();
        signalRServiceRef.current = signalRService;

        signalRService.onClubStatusChange((clubId, message) => {
          common.AntDesignMessage.loading(message);
          setTimeout(() => {
            handleClubChangeCallback(clubId);
          }, 2000);
        });

        signalRService.onPermissionStatusChange((finalResult, message) => {
          common.AntDesignMessage.loading(message);
          setTimeout(() => {
            handlePermissionChangeCallback(finalResult, message);
          }, 2000);
        });

       signalRService.onForceLogout((message) => {
          setTimeout(() => {
            common.SweetAlert.alert(message);
            setTimeout(() => {
              common.clearUser();     // clear user
              common.clearToken();    // clear token
              window.location.href = "/login";
            }, 2000); 
          }, 1000);
        });

        const connected = await signalRService.initialize(user.id);
        if (!connected) {
          console.warn("[AuthContext] SignalR connection failed");
        }
      } catch (error) {
        console.error("[AuthContext] Error initializing SignalR:", error);
      }
    };

    initializeSignalR();

    return () => {
      if (signalRServiceRef.current) {
        signalRServiceRef.current.disconnect();
        signalRServiceRef.current = null;
      }
    };
  }, [user?.id, handleClubChangeCallback, handlePermissionChangeCallback]);

  /**
   * Initialize authentication state on mount
   */
  useLayoutEffect(() => {
    const initializeAuth = async () => {
      try {
        const _user = common.getUser();
        global.user = _user;

        if (_user) {
          currentClubIdRef.current = _user.defaultClubId;
          global.myClub = _user.myClubs?.find(
            (item) => item.id === _user.defaultClubId
          );
        }

        setUser(_user);

        if (_user?.menus?.length > 0) {
          await rebuildAppRouter();
          setRoutesReady(true);
        } else {
          setRoutesReady(true);
        }

        if (window.location.href.includes("app") && (!_user || !_user.id)) {
          window.location.replace("/login");
        }
      } catch (error) {
        console.error("[AuthContext] Auth initialization error:", error);
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []);

  return (
    <AuthContext.Provider
      value={{ user, setUser, isLoading, routesReady, setRoutesReady }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);

